import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { KeyboardArrowDownRounded } from "@mui/icons-material";

import { Popper, Text } from "@fragments";

import { getSubscriptionStatusLabels, SUBSCRIPTION_STATUS_TONE } from "../../../enums";

import "./styles.scss";

function normalizeList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") return Object.values(raw);
  return [];
}

function sortByPriority(subs) {
  const score = (sub) => (sub?.status === "active" ? 0 : 1);
  return [...subs].sort((a, b) => score(a) - score(b));
}

function getPlanLabel(planId, plans, t) {
  const plan = plans?.find?.((p) => p.id === planId);
  return (
    plan?.label ||
    t("membersList.subscription.planFallback", { id: planId ?? "" }).trim()
  );
}

function formatDate(raw) {
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function getStatusDateSuffix(sub, t) {
  if (sub?.status === "active") {
    const formatted = formatDate(sub?.timestamps?.expiration);
    return formatted
      ? t("membersList.subscription.activeUntil", { date: formatted })
      : null;
  }
  if (sub?.status === "canceled") {
    const formatted = formatDate(sub?.timestamps?.canceled);
    return formatted
      ? t("membersList.subscription.canceledOn", { date: formatted })
      : null;
  }
  return null;
}

function resolveSubscriptionParts(sub, plans, t, statusLabels) {
  const tone = SUBSCRIPTION_STATUS_TONE[sub.status] || "neutral";
  const planLabel = sub.plan?.label || getPlanLabel(sub.plan_id, plans, t);
  const statusLabel =
    statusLabels[sub.status] || t("membersList.subscriptionStatuses.pending");
  const dateSuffix = getStatusDateSuffix(sub, t);
  return { tone, planLabel, statusLabel, dateSuffix };
}

function SubscriptionBadge({ sub, plans, t, statusLabels }) {
  const { tone, planLabel, statusLabel, dateSuffix } = resolveSubscriptionParts(
    sub,
    plans,
    t,
    statusLabels
  );

  return (
    <div className={`members-list-subscription members-list-subscription--${tone}`}>
      <Text.span contrast={8}>
        {planLabel}
      </Text.span>
      <span className="members-list-subscription__dot" />
      <Text.span contrast={4}>
        {dateSuffix ? `${statusLabel} ${dateSuffix}` : statusLabel}
      </Text.span>
    </div>
  );
}

export default function SubscriptionCell({ member, plans }) {
  const { t } = useTranslation("memberModals");
  const popperRef = useRef(null);

  const statusLabels = useMemo(() => getSubscriptionStatusLabels(t), [t]);

  const subscriptions = sortByPriority(
    normalizeList(member?.subscriptions ?? member?.subscription)
  );

  if (!subscriptions.length) {
    return <Text.span contrast={6}>—</Text.span>;
  }

  const [primary, ...rest] = subscriptions;
  const extra = rest.length;

  function openPopper(event) {
    event.stopPropagation();
    const options = subscriptions.map((sub, index) => {
      const { planLabel, statusLabel, dateSuffix } = resolveSubscriptionParts(
        sub,
        plans,
        t,
        statusLabels
      );
      return {
        id: sub.id ?? `${sub.plan_id}-${index}`,
        label: planLabel,
        right: (
          <Text.span contrast={4}>
            {dateSuffix ? `${statusLabel} ${dateSuffix}` : statusLabel}
          </Text.span>
        ),
        noHover: true,
      };
    });

    popperRef.current?.open(options, "bottom-end", event.currentTarget);
  }

  return (
    <div className="members-list-subscription-cell">
      <SubscriptionBadge
        sub={primary}
        plans={plans}
        t={t}
        statusLabels={statusLabels}
      />
      {extra > 0 && (
        <>
          <button
            type="button"
            className="members-list-subscription-more"
            onClick={openPopper}
          >
            <Text.span contrast={6}>
              {t("membersList.subscription.more", { count: extra })}
            </Text.span>
            <KeyboardArrowDownRounded className="members-list-subscription-more__caret" />
          </button>
          <Popper controlRef={popperRef} />
        </>
      )}
    </div>
  );
}
